import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import CanUser from '@frontend/components/CanUser';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import { SelfConnectionProvider } from '@frontend/contexts';
import { buildMessagesPath } from '@frontend/utils/messagingUrl';

const Chat = lazy(() =>
  import(/* webpackChunkName: "Chat" */ '@frontend/features/chat').then(m => ({
    default: m.Chat,
  }))
);

// Layout shared by /chat and /messages so the federated component mounts once (a
// remount would re-init sockets, refetch, and flash; the singleton jotai store
// also makes remounts fragile). Child routes are parse-only.
export function ChatShell() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Legacy deep links (old notifications, delivered push, emails, bookmarks) use
  // /chat?tab=messages&conversationId=…; redirect them to the new /messages URL.
  if (location.pathname.endsWith('/chat') && searchParams.get('tab') === 'messages') {
    const instanceBase = location.pathname.replace(/\/chat$/, '');
    const target = buildMessagesPath({
      conversationId: searchParams.get('conversationId') ?? undefined,
      threadPanelId: searchParams.get('msgThreadId') ?? undefined,
      targetMessageId: searchParams.get('messageId') ?? undefined,
    });
    return <Navigate replace to={`${instanceBase}${target}`} />;
  }

  return (
    <CanUser operations={['use_chat']}>
      <SelfConnectionProvider>
        <Suspense fallback={<FullScreenLoader />}>
          <Chat />
        </Suspense>
      </SelfConnectionProvider>
    </CanUser>
  );
}
